/**
 * MyQuoteRequestsPage - Track all outgoing quote requests
 * Shows quote requests sent to contractors and verifiers
 * Includes price response display, accept/counter/decline actions, negotiation timeline
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Grid,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Send,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  Visibility,
  Person,
  Security,
  Schedule,
  LocationOn,
  Refresh,
  SwapHoriz,
  ThumbUp,
  ThumbDown,
  Message,
  EventAvailable
} from '@mui/icons-material';
import { TextField, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import CallOutFeePanel from '../components/visits/CallOutFeePanel';
import { brand, text, border, bg } from '../theme/colors';
import { formatHarareWindow, harareInputToIso, isoToHarareInput, windowStart } from '../utils/time';
import NegotiationTimeline from '../components/quotes/NegotiationTimeline';
import CounterOfferDialog from '../components/quotes/CounterOfferDialog';
import QuoteAttachments from '../components/quotes/QuoteAttachments';
import { renderMarkdown } from '../components/documents/documentStyles';
import DialogCloseButton from '../components/common/DialogCloseButton';
import { AffordabilityBanner, QuoteNegotiationActions } from '../components/QuoteAffordability';

const MyQuoteRequestsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [tabValue, setTabValue] = useState(0); // 0 = Contractors, 1 = Verifiers
  const [statusFilter, setStatusFilter] = useState('all');
  const [contractorRequests, setContractorRequests] = useState([]);
  const [verifierRequests, setVerifierRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [counterOfferDialogOpen, setCounterOfferDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'accept' | 'decline', request }
  const [chosenVisitWindow, setChosenVisitWindow] = useState('');
  const [visitAccessNotes, setVisitAccessNotes] = useState('');
  const [confirmingVisit, setConfirmingVisit] = useState(false);
  const [showProposeEditor, setShowProposeEditor] = useState(false);
  const [proposeRows, setProposeRows] = useState([{ start: '', end: '' }, { start: '', end: '' }, { start: '', end: '' }]);
  const [proposeAccessNotes, setProposeAccessNotes] = useState('');
  const [proposeVisitType, setProposeVisitType] = useState('in_person');
  const [proposingVisit, setProposingVisit] = useState(false);
  const [showRescheduleEditor, setShowRescheduleEditor] = useState(false);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [markingVisitDone, setMarkingVisitDone] = useState(false);

  const handleConfirmVisit = async () => {
    if (!chosenVisitWindow) return;
    setConfirmingVisit(true);
    try {
      const response = await api.put(`/quote-requests/${selectedRequest.id}/accept-visit`, {
        visitWindow: chosenVisitWindow,
        // undefined (not null) when empty — the API validator only skips absent fields
        accessNotes: visitAccessNotes || undefined
      });
      if (response.data.success) {
        // Merge: the API returns the raw row, which lacks the joined display
        // fields (contractor_name etc.) the dialog also renders.
        setSelectedRequest((prev) => ({ ...prev, ...response.data.data.quoteRequest }));
        setChosenVisitWindow('');
        setVisitAccessNotes('');
        loadQuoteRequests();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to confirm site visit');
    } finally {
      setConfirmingVisit(false);
    }
  };

  const parseVisitWindows = (request) => {
    const raw = request?.proposed_visit_windows;
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw || '[]'); } catch { return []; }
  };

  // Visit times are shown in the property's timezone (Harare / CAT), not the
  // viewer's, so client and contractor always see the same labelled number.
  const formatVisitWindow = formatHarareWindow;

  // The provider proposed site-visit times and is waiting for the owner to
  // confirm one — surfaced as a list badge (legacy rows with windows but no
  // visit_proposed_by are provider-proposed too).
  const hasPendingProviderVisit = (request) => (
    parseVisitWindows(request).length > 0
    && request?.visit_proposed_by !== 'client'
    && !request?.accepted_visit_window
    && ['pending', 'responded', 'negotiating'].includes(request?.status)
  );

  // A quote-request time can be a site visit or a client call (null = legacy visit).
  const isCallRequest = (request) => request?.visit_type === 'client_call';
  const qrVisitLabel = (request) => (isCallRequest(request) ? 'Client call' : 'Site visit');

  const openRequestDetail = (request) => {
    setShowProposeEditor(false);
    setShowRescheduleEditor(false);
    setRescheduleTime('');
    setChosenVisitWindow('');
    setVisitAccessNotes('');
    setProposeVisitType('in_person');
    setSelectedRequest(request);
  };

  // One-step reschedule of a confirmed visit/call — records what was
  // re-agreed in chat and notifies the other party. No re-propose loop.
  const handleRescheduleVisit = async () => {
    if (!rescheduleTime) return;
    setRescheduling(true);
    try {
      const response = await api.put(`/quote-requests/${selectedRequest.id}/reschedule-visit`, {
        newTime: harareInputToIso(rescheduleTime)
      });
      if (response.data.success) {
        setSelectedRequest((prev) => ({ ...prev, ...response.data.data.quoteRequest }));
        setShowRescheduleEditor(false);
        setRescheduleTime('');
        loadQuoteRequests();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reschedule');
    } finally {
      setRescheduling(false);
    }
  };

  // Owner attests a passed visit/call took place — no proof needed (they were
  // at the property); the contractor is nudged for the quotation.
  const handleMarkVisitDone = async () => {
    setMarkingVisitDone(true);
    try {
      const response = await api.post(`/quote-requests/${selectedRequest.id}/complete-visit`);
      if (response.data.success) {
        setSelectedRequest((prev) => ({ ...prev, ...response.data.data.quoteRequest }));
        loadQuoteRequests();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to mark the visit as done');
    } finally {
      setMarkingVisitDone(false);
    }
  };

  useEffect(() => {
    const rid = searchParams.get('request');
    const feeSession = searchParams.get('fee_session');
    if (!rid || !feeSession) return;
    // The panel's checkout uses /api/visit-fees; the legacy endpoint below
    // settles sessions started before that shipped, and both are idempotent.
    const source = searchParams.get('fee_source') || 'quote_request';
    const feeId = searchParams.get('fee_id') || rid;
    api.post(`/visit-fees/${source}/${feeId}/confirm`, { sessionId: feeSession })
      .then(() => loadQuoteRequests())
      .catch(() => { /* webhook will still settle it */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // accepted_visit_window stores only the start; recover the full window
  // (with its end time) from the proposed list for display.
  const formatAcceptedVisit = (request) => {
    const accepted = request.accepted_visit_window;
    const match = parseVisitWindows(request).find(
      (w) => new Date(windowStart(w)).getTime() === new Date(accepted).getTime()
    );
    return formatVisitWindow(match || accepted);
  };

  const handleProposeVisit = async () => {
    // Picker values are wall-clock strings the owner means as Harare time;
    // convert to UTC ISO anchored to CAT before sending.
    const windows = proposeRows
      .filter((row) => row.start)
      .map((row) => (row.end
        ? { start: harareInputToIso(row.start), end: harareInputToIso(row.end) }
        : harareInputToIso(row.start)));
    if (windows.length === 0) return;
    setProposingVisit(true);
    try {
      const response = await api.put(`/quote-requests/${selectedRequest.id}/propose-visit`, {
        visitWindows: windows,
        visitType: proposeVisitType,
        // undefined (not null) when empty — the API validator only skips absent fields
        accessNotes: (proposeVisitType === 'in_person' && proposeAccessNotes) || undefined
      });
      if (response.data.success) {
        setSelectedRequest((prev) => ({ ...prev, ...response.data.data.quoteRequest }));
        setShowProposeEditor(false);
        setProposeRows([{ start: '', end: '' }, { start: '', end: '' }, { start: '', end: '' }]);
        setProposeAccessNotes('');
        loadQuoteRequests();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send visit times');
    } finally {
      setProposingVisit(false);
    }
  };

  useEffect(() => {
    loadQuoteRequests();
  }, []);

  // Deep-link from a "quote response / visit times proposed" notification:
  // /my-quote-requests?request=<id> opens that request straight to its details.
  useEffect(() => {
    const rid = searchParams.get('request');
    if (!rid) return;
    const all = [...contractorRequests, ...verifierRequests];
    const match = all.find((q) => String(q.id) === String(rid));
    if (match) {
      setTabValue(match.target_type === 'verifier' ? 1 : 0);
      openRequestDetail(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, contractorRequests, verifierRequests]);

  const loadQuoteRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/quote-requests');
      const allRequests = response.data.data?.quoteRequests || [];

      // Split by target type
      setContractorRequests(allRequests.filter(r => r.target_type !== 'verifier'));
      setVerifierRequests(allRequests.filter(r => r.target_type === 'verifier'));
    } catch (err) {
      console.error('Error loading quote requests:', err);
      setError('Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  };

  const [acceptedHire, setAcceptedHire] = useState(null);

  const handleAccept = async (request) => {
    try {
      const response = await api.put(`/quote-requests/${request.id}/accept`);
      if (response.data?.data?.hire) {
        setAcceptedHire(response.data.data.hire);
      }
      setSelectedRequest(null);
      loadQuoteRequests();
    } catch (err) {
      console.error('Accept error:', err);
      setError(err.response?.data?.message || 'Failed to accept quote');
    }
  };

  const handleDecline = async (request) => {
    try {
      await api.put(`/quote-requests/${request.id}/decline`);
      setSelectedRequest(null);
      loadQuoteRequests();
    } catch (err) {
      console.error('Decline error:', err);
      setError(err.response?.data?.message || 'Failed to decline quote');
    }
  };

  const handleWithdraw = async (request) => {
    try {
      await api.put(`/quote-requests/${request.id}/cancel`);
      setSelectedRequest(null);
      loadQuoteRequests();
    } catch (err) {
      console.error('Withdraw error:', err);
      setError(err.response?.data?.message || 'Failed to withdraw quote request');
    }
  };

  const getStatusChipSx = (status) => {
    switch (status) {
      case 'pending':
        return { bgcolor: '#fffbeb', color: '#FAA61A', border: '1px solid #fde68a' };
      case 'responded':
        return { bgcolor: '#eff6ff', color: brand.primary, border: '1px solid #bfdbfe' };
      case 'negotiating':
        return { bgcolor: '#fffbeb', color: '#f59e0b', border: '1px solid #fde68a' };
      case 'accepted':
      case 'completed':
        return { bgcolor: '#f0fdf4', color: '#43B581', border: '1px solid #bbf7d0' };
      case 'declined':
      case 'cancelled':
        return { bgcolor: '#fdf2f2', color: '#e74c3c', border: '1px solid #fecaca' };
      default:
        return {};
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <HourglassEmpty fontSize="small" />,
      responded: <Send fontSize="small" />,
      negotiating: <SwapHoriz fontSize="small" />,
      accepted: <CheckCircle fontSize="small" />,
      declined: <Cancel fontSize="small" />,
      cancelled: <Cancel fontSize="small" />,
      completed: <CheckCircle fontSize="small" />
    };
    return icons[status];
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const filteredContractorRequests = contractorRequests.filter(req =>
    statusFilter === 'all' || req.status === statusFilter
  );

  const filteredVerifierRequests = verifierRequests.filter(req =>
    statusFilter === 'all' || req.status === statusFilter
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setStatusFilter('all');
  };

  const getTargetRole = (request) => (
    request.target_type === 'verifier' ? 'verifier' : 'contractor'
  );

  const getLatestOfferRole = (request) => (
    request.latest_offer_role
    || (request.status === 'responded' ? getTargetRole(request) : null)
  );

  const isClientTurn = (request) => {
    const latestOfferRole = getLatestOfferRole(request);
    return ['responded', 'negotiating'].includes(request.status)
      && latestOfferRole
      && latestOfferRole !== 'client';
  };

  const renderRequestsTable = (requests, isVerifier = false) => {
    if (requests.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No {isVerifier ? 'verifier' : 'contractor'} quote requests found.
          Go to "Find {isVerifier ? 'Verifiers' : 'Contractors'}" to request quotes.
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: bg.subtle }}>
              <TableCell><strong>{isVerifier ? 'Verifier' : 'Contractor'}</strong></TableCell>
              <TableCell><strong>Project Description</strong></TableCell>
              <TableCell><strong>Location</strong></TableCell>
              <TableCell><strong>Your Budget</strong></TableCell>
              <TableCell><strong>Their Quote</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Sent</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => {
              const targetName = isVerifier
                ? (request.verifier_name || request.verifier_display_name || 'Verifier')
                : (request.contractor_name || request.contractor_display_name || 'Contractor');
              const targetEmail = isVerifier ? request.verifier_email : request.contractor_email;

              return (
                <TableRow key={request.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: isVerifier ? brand.secondary : brand.primary, width: 32, height: 32 }}>
                        {isVerifier ? <Security fontSize="small" /> : <Person fontSize="small" />}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{targetName}</Typography>
                        <Typography variant="caption" color="text.secondary">{targetEmail}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {request.project_description || request.project_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2">{request.project_location || '-'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatCurrency(request.estimated_budget, request.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ color: request.quote_amount ? '#43B581' : 'text.secondary' }}>
                      {!request.quote_amount && ['responded', 'negotiating'].includes(request.status)
                        ? 'Site visit first'
                        : formatCurrency(request.quote_amount, request.quote_currency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status?.toUpperCase()}
                      size="small"
                      icon={getStatusIcon(request.status)}
                      sx={getStatusChipSx(request.status)}
                    />
                    {hasPendingProviderVisit(request) && (
                      <Chip
                        label="Confirm visit times"
                        size="small"
                        icon={<EventAvailable fontSize="small" />}
                        onClick={() => openRequestDetail(request)}
                        sx={{
                          mt: 0.5,
                          display: 'flex',
                          cursor: 'pointer',
                          bgcolor: '#ecfdf5',
                          color: '#0f766e',
                          border: '1px solid #99f6e4',
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: '#0f766e' }
                        }}
                      />
                    )}
                    {request.accepted_visit_window && ['pending', 'responded', 'negotiating'].includes(request.status) && (
                      <Chip
                        label={request.visit_completed_at
                          ? (isCallRequest(request) ? 'Call completed' : 'Visit completed')
                          : (isCallRequest(request) ? 'Call confirmed' : 'Visit confirmed')}
                        size="small"
                        icon={<EventAvailable fontSize="small" />}
                        onClick={() => openRequestDetail(request)}
                        sx={{
                          mt: 0.5,
                          display: 'flex',
                          cursor: 'pointer',
                          bgcolor: '#f0fdf4',
                          color: '#15803d',
                          border: '1px solid #bbf7d0',
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: '#15803d' }
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(request.created_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => openRequestDetail(request)}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Stats cards
  const allRequests = [...contractorRequests, ...verifierRequests];
  const stats = {
    total: allRequests.length,
    pending: allRequests.filter(r => r.status === 'pending').length,
    responded: allRequests.filter(r => ['responded', 'negotiating'].includes(r.status)).length,
    accepted: allRequests.filter(r => r.status === 'accepted').length
  };

  // Determine target info for selected request
  const selectedIsVerifier = selectedRequest?.target_type === 'verifier';
  const selectedTargetName = selectedIsVerifier
    ? (selectedRequest?.verifier_name || selectedRequest?.verifier_display_name || 'Verifier')
    : (selectedRequest?.contractor_name || selectedRequest?.contractor_display_name || 'Contractor');

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <AffordabilityBanner sx={{ mb: 2 }} />
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            My Quote Requests
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track all quote requests sent to contractors and verifiers
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadQuoteRequests}
          sx={{ borderColor: brand.primary, color: brand.primary, borderWidth: 2, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'white', border: `2px solid ${border.light}`, borderRadius: 2 }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: brand.primary }}>{stats.total}</Typography>
            <Typography variant="body2" sx={{ color: text.secondary }}>Total Sent</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'white', border: `2px solid ${border.light}`, borderRadius: 2 }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#FAA61A' }}>{stats.pending}</Typography>
            <Typography variant="body2" sx={{ color: text.secondary }}>Awaiting Response</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'white', border: `2px solid ${border.light}`, borderRadius: 2 }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#43B581' }}>{stats.responded}</Typography>
            <Typography variant="body2" sx={{ color: text.secondary }}>Responses Received</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'white', border: `2px solid ${border.light}`, borderRadius: 2 }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#43B581' }}>{stats.accepted}</Typography>
            <Typography variant="body2" sx={{ color: text.secondary }}>Accepted</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ '& .MuiTab-root': { minHeight: 56 } }}
        >
          <Tab icon={<Person />} iconPosition="start" label={`Contractor Requests (${contractorRequests.length})`} />
          <Tab icon={<Security />} iconPosition="start" label={`Verifier Requests (${verifierRequests.length})`} />
        </Tabs>
      </Paper>

      {/* Status Filter */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {['all', 'pending', 'responded', 'negotiating', 'accepted', 'declined'].map((status) => (
          <Chip
            key={status}
            label={status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            onClick={() => setStatusFilter(status)}
            color={statusFilter === status ? 'primary' : 'default'}
            variant={statusFilter === status ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      {/* Loading & Error States */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {acceptedHire && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setAcceptedHire(null)}
          action={
            <Button color="inherit" size="small" onClick={() => navigate(`/projects/${acceptedHire.projectId}`)}>
              View Project
            </Button>
          }
        >
          Contractor hired successfully! Quote of ${parseFloat(acceptedHire.agreedAmount || 0).toFixed(2)} accepted.
        </Alert>
      )}

      {/* Content */}
      {!loading && (
        <>
          {tabValue === 0 && renderRequestsTable(filteredContractorRequests, false)}
          {tabValue === 1 && renderRequestsTable(filteredVerifierRequests, true)}
        </>
      )}

      {/* Quote Request Detail Dialog (Enhanced with pricing + actions) */}
      <Dialog
        open={!!selectedRequest && !counterOfferDialogOpen}
        onClose={() => setSelectedRequest(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setSelectedRequest(null)} />
        {selectedRequest && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedIsVerifier ? <Security sx={{ color: brand.secondary }} /> : <Person sx={{ color: brand.primary }} />}
              Quote Request Details
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                {/* The page-level error banner sits BEHIND this dialog — failures
                    must also surface here or actions look like they did nothing. */}
                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
                )}

                {/* Target Info */}
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    {selectedIsVerifier ? 'Verifier' : 'Contractor'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                    <Avatar sx={{ bgcolor: selectedIsVerifier ? brand.secondary : brand.primary }}>
                      {selectedIsVerifier ? <Security /> : <Person />}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>{selectedTargetName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedIsVerifier ? selectedRequest.verifier_email : selectedRequest.contractor_email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider />

                {/* Project Info */}
                <Box>
                  <Typography variant="overline" color="text.secondary">Project</Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                    {selectedRequest.project_name || selectedRequest.project_description || '-'}
                  </Typography>
                  {selectedRequest.project_description && selectedRequest.project_name && (
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, mt: 1, bgcolor: bg.subtle, maxHeight: 400, overflowY: 'auto' }}
                    >
                      {renderMarkdown(selectedRequest.project_description)}
                    </Paper>
                  )}
                </Box>

                {/* Details Grid */}
                <Grid container spacing={2}>
                  {selectedRequest.project_location && (
                    <Grid item xs={6}>
                      <Typography variant="overline" color="text.secondary">Location</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="body2">{selectedRequest.project_location}</Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Typography variant="overline" color="text.secondary">Your Budget</Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                      {formatCurrency(selectedRequest.estimated_budget, selectedRequest.currency)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="overline" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedRequest.status?.toUpperCase()}
                        size="small"
                        icon={getStatusIcon(selectedRequest.status)}
                        sx={getStatusChipSx(selectedRequest.status)}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="overline" color="text.secondary">Sent</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <Schedule fontSize="small" color="action" />
                      <Typography variant="body2">{formatDate(selectedRequest.created_at)}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Additional Notes */}
                {selectedRequest.additional_notes && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="overline" color="text.secondary">Additional Notes</Typography>
                      <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: bg.subtle }}>
                        <Typography variant="body2">{selectedRequest.additional_notes}</Typography>
                      </Paper>
                    </Box>
                  </>
                )}

                {/* Quote Response (when responded/negotiating/accepted) */}
                {selectedRequest.quote_amount && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        {selectedIsVerifier ? 'Verifier' : 'Contractor'} Quote Response
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          mt: 0.5,
                          bgcolor: selectedRequest.status === 'accepted' ? '#f0fdf4' : '#eff6ff',
                          borderColor: selectedRequest.status === 'accepted' ? '#bbf7d0' : '#bfdbfe'
                        }}
                      >
                        <Typography variant="h5" fontWeight={700} sx={{ color: brand.primary, mb: 1 }}>
                          {formatCurrency(selectedRequest.quote_amount, selectedRequest.quote_currency)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          {selectedRequest.proposed_start_date && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Proposed Start</Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {formatDate(selectedRequest.proposed_start_date)}
                              </Typography>
                            </Box>
                          )}
                          {selectedRequest.estimated_timeline_days && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Timeline</Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {selectedRequest.estimated_timeline_days} days
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        {selectedRequest.response_message && (
                          <Typography variant="body2" sx={{ mt: 1.5, fontStyle: 'italic' }}>
                            "{selectedRequest.response_message}"
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  </>
                )}

                {/* Site Visit — works whether or not there's a price on the table.
                    Either side can propose times; the other confirms one. Site
                    visits are always free. Legacy rows with windows but no
                    visit_proposed_by are provider-proposed. */}
                {(['pending', 'responded', 'negotiating'].includes(selectedRequest.status) || selectedRequest.accepted_visit_window) && (() => {
                  const visitWindows = parseVisitWindows(selectedRequest);
                  const proposedBy = selectedRequest.visit_proposed_by
                    || (visitWindows.length > 0 ? 'provider' : null);
                  const siteVisitFirst = !selectedRequest.quote_amount
                    && ['responded', 'negotiating'].includes(selectedRequest.status);
                  return (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="overline" color="text.secondary">
                          {siteVisitFirst
                            ? `${qrVisitLabel(selectedRequest)} Requested${selectedRequest.visit_fee_amount ? '' : ' — Free'}`
                            : qrVisitLabel(selectedRequest)}
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2.5, mt: 0.5, bgcolor: selectedRequest.accepted_visit_window ? '#f0fdf4' : '#fffbeb', borderColor: selectedRequest.accepted_visit_window ? '#bbf7d0' : '#fde68a' }}>
                          {siteVisitFirst && (
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              {selectedRequest.visit_fee_amount
                                ? <>The {selectedIsVerifier ? 'verifier' : 'contractor'} needs {isCallRequest(selectedRequest) ? 'a call with you' : 'to see the site'} before giving you a price — you'll get the full quote after the {isCallRequest(selectedRequest) ? 'call' : 'visit'}.</>
                                : (isCallRequest(selectedRequest)
                                  ? <>The {selectedIsVerifier ? 'verifier' : 'contractor'} needs a call with you before giving you a price. <strong>Calls and site visits are free</strong> — you'll get the full quote after the call.</>
                                  : <>The {selectedIsVerifier ? 'verifier' : 'contractor'} needs to see the site before giving you a price. <strong>Site visits are free</strong> — you'll get the full quote after the visit.</>)}
                            </Typography>
                          )}

                          {/* Call-out fee: pay, cancel, confirm or dispute —
                              one panel shared with project visits. */}
                          <CallOutFeePanel
                            source="quote_request"
                            request={selectedRequest}
                            providerName={selectedTargetName}
                            onChanged={loadQuoteRequests}
                          />

                          {siteVisitFirst && selectedRequest.response_message && (
                            <Typography variant="body2" sx={{ mb: 1.5, fontStyle: 'italic' }}>
                              "{selectedRequest.response_message}"
                            </Typography>
                          )}

                          {selectedRequest.accepted_visit_window && selectedRequest.visit_completed_at ? (
                            <Alert severity="success" icon={<EventAvailable />}>
                              {qrVisitLabel(selectedRequest)} completed — <strong>{formatAcceptedVisit(selectedRequest)}</strong>.
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {selectedRequest.visit_completed_by === 'client'
                                  ? <>You confirmed this {isCallRequest(selectedRequest) ? 'call' : 'visit'} took place — {selectedTargetName} can now price the work. Expect their quotation next.</>
                                  : <>{selectedTargetName} completed this {isCallRequest(selectedRequest) ? 'call' : 'visit'} and can now price the work — expect their quotation next.</>}
                              </Typography>
                              {Array.isArray(selectedRequest.visit_photo_urls) && selectedRequest.visit_photo_urls.length > 0 && (
                                <>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Photos taken at the site — click to view full size:
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                    {selectedRequest.visit_photo_urls.map((url, index) => (
                                      <Box
                                        key={url}
                                        component="img"
                                        src={url}
                                        alt={`Visit photo ${index + 1}`}
                                        onClick={() => window.open(url, '_blank', 'noopener')}
                                        sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', border: '1px solid #bbf7d0' }}
                                      />
                                    ))}
                                  </Box>
                                </>
                              )}
                            </Alert>
                          ) : selectedRequest.accepted_visit_window ? (
                            <Alert severity="success" icon={<EventAvailable />}>
                              {qrVisitLabel(selectedRequest)} confirmed for <strong>{formatAcceptedVisit(selectedRequest)}</strong>.
                              {Number(selectedRequest.visit_rescheduled_count) > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  (rescheduled {selectedRequest.visit_rescheduled_count}×)
                                </Typography>
                              )}
                              {selectedRequest.visit_access_notes && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  Your access instructions: "{selectedRequest.visit_access_notes}"
                                </Typography>
                              )}
                              {!showRescheduleEditor ? (
                                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {new Date(selectedRequest.accepted_visit_window).getTime() <= Date.now() && (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      disabled={markingVisitDone}
                                      onClick={handleMarkVisitDone}
                                      sx={{ bgcolor: '#43B581', '&:hover': { bgcolor: '#3aa370' } }}
                                    >
                                      {markingVisitDone ? 'Saving…' : `Yes, the ${isCallRequest(selectedRequest) ? 'call' : 'visit'} happened`}
                                    </Button>
                                  )}
                                  <Button size="small" variant="outlined" color="success" onClick={() => { setRescheduleTime(isoToHarareInput(selectedRequest.accepted_visit_window)); setShowRescheduleEditor(true); }}>
                                    Reschedule
                                  </Button>
                                </Box>
                              ) : (
                                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <TextField
                                    type="datetime-local"
                                    size="small"
                                    label="New time (Zimbabwe time)"
                                    value={rescheduleTime}
                                    onChange={(e) => setRescheduleTime(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                  <Button size="small" variant="contained" disabled={!rescheduleTime || rescheduling} onClick={handleRescheduleVisit}>
                                    {rescheduling ? 'Saving…' : 'Save'}
                                  </Button>
                                  <Button size="small" onClick={() => setShowRescheduleEditor(false)}>Cancel</Button>
                                </Box>
                              )}
                            </Alert>
                          ) : proposedBy === 'client' && visitWindows.length > 0 ? (
                            <>
                              <Alert severity="info" icon={<EventAvailable />} sx={{ mb: 1 }}>
                                You proposed these {isCallRequest(selectedRequest) ? 'call' : 'visit'} times — waiting for {selectedTargetName} to confirm one.
                              </Alert>
                              {visitWindows.map((w) => (
                                <Typography key={windowStart(w)} variant="body2" sx={{ ml: 0.5 }}>
                                  • {formatVisitWindow(w)}
                                </Typography>
                              ))}
                              <Button size="small" sx={{ mt: 1 }} onClick={() => setShowProposeEditor(true)}>
                                Change proposed times
                              </Button>
                            </>
                          ) : proposedBy === 'provider' && visitWindows.length > 0 ? (
                            <>
                              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Pick a {isCallRequest(selectedRequest) ? 'call' : 'visit'} time:</Typography>
                              <RadioGroup value={chosenVisitWindow} onChange={(e) => setChosenVisitWindow(e.target.value)}>
                                {visitWindows.map((w) => (
                                  <FormControlLabel
                                    key={windowStart(w)}
                                    value={windowStart(w)}
                                    control={<Radio size="small" />}
                                    label={formatVisitWindow(w)}
                                  />
                                ))}
                              </RadioGroup>
                              {!isCallRequest(selectedRequest) && (
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  sx={{ mt: 1.5 }}
                                  label="Access instructions for the visit (optional)"
                                  value={visitAccessNotes}
                                  onChange={(e) => setVisitAccessNotes(e.target.value)}
                                  placeholder="e.g. My caretaker Tapiwa will open for you — call him on 077… when you arrive. Gate code 4321."
                                  helperText="Shared with the contractor only after you confirm the visit — include who will be there, their phone, key or gate codes."
                                />
                              )}
                              <Button
                                variant="contained"
                                startIcon={<EventAvailable />}
                                disabled={!chosenVisitWindow || confirmingVisit}
                                onClick={handleConfirmVisit}
                                sx={{ mt: 1.5, bgcolor: '#43B581', '&:hover': { bgcolor: '#3aa370' } }}
                              >
                                Confirm {qrVisitLabel(selectedRequest)}
                              </Button>
                            </>
                          ) : !showProposeEditor ? (
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                No visit or call times on the table yet. Propose dates that work for you and
                                the {selectedIsVerifier ? 'verifier' : 'contractor'} will confirm one.
                              </Typography>
                              <Button
                                variant="contained"
                                startIcon={<EventAvailable />}
                                onClick={() => setShowProposeEditor(true)}
                                sx={{ bgcolor: brand.primary }}
                              >
                                Propose Visit or Call Times
                              </Button>
                            </>
                          ) : null}

                          {showProposeEditor && !selectedRequest.accepted_visit_window && (
                            <Box sx={{ mt: 2 }}>
                              {/* A time slot can be a physical site visit or a phone/video call —
                                  the labels and access-notes follow the choice everywhere. */}
                              <RadioGroup
                                row
                                value={proposeVisitType}
                                onChange={(e) => setProposeVisitType(e.target.value)}
                                sx={{ mb: 1 }}
                              >
                                <FormControlLabel value="in_person" control={<Radio size="small" />} label="Site visit" />
                                <FormControlLabel value="client_call" control={<Radio size="small" />} label="Phone / video call" />
                              </RadioGroup>
                              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Propose up to three times, in Zimbabwe (Harare / CAT) time. Add an end time to offer a window (e.g. "any time between 8am and midday") instead of an exact time.
                              </Typography>
                              {proposeRows.map((row, index) => (
                                <Box key={index} sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                                  <TextField
                                    label={`Option ${index + 1}`}
                                    type="datetime-local"
                                    size="small"
                                    value={row.start}
                                    onChange={(e) => {
                                      const next = [...proposeRows];
                                      next[index] = { ...next[index], start: e.target.value };
                                      setProposeRows(next);
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 200, flex: 1 }}
                                  />
                                  <TextField
                                    label="Until (optional)"
                                    type="datetime-local"
                                    size="small"
                                    value={row.end}
                                    onChange={(e) => {
                                      const next = [...proposeRows];
                                      next[index] = { ...next[index], end: e.target.value };
                                      setProposeRows(next);
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 200, flex: 1 }}
                                  />
                                </Box>
                              ))}
                              {proposeVisitType === 'in_person' && (
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  label="Access instructions for the visit (optional)"
                                  value={proposeAccessNotes}
                                  onChange={(e) => setProposeAccessNotes(e.target.value)}
                                  placeholder="e.g. My caretaker Tapiwa will open for you — call him on 077… when you arrive. Gate code 4321."
                                  helperText="Shared with the contractor once a time is confirmed."
                                />
                              )}
                              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                                <Button
                                  variant="contained"
                                  startIcon={<EventAvailable />}
                                  disabled={!proposeRows.some((row) => row.start) || proposingVisit}
                                  onClick={handleProposeVisit}
                                  sx={{ bgcolor: '#43B581', '&:hover': { bgcolor: '#3aa370' } }}
                                >
                                  {proposeVisitType === 'client_call' ? 'Send Call Times' : 'Send Visit Times'}
                                </Button>
                                <Button onClick={() => setShowProposeEditor(false)}>Cancel</Button>
                              </Box>
                            </Box>
                          )}
                        </Paper>
                      </Box>
                    </>
                  );
                })()}

                {/* Attachments — plans, photos, documents shared inside the request */}
                <Divider />
                <QuoteAttachments quoteRequestId={selectedRequest.id} />

                {/* Negotiation Timeline */}
                {(selectedRequest.status === 'negotiating' || Number(selectedRequest.negotiation_round) > 1) && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="overline" color="text.secondary">Negotiation History</Typography>
                      <Box sx={{ mt: 1 }}>
                        <NegotiationTimeline
                          quoteRequestId={selectedRequest.id}
                          maxRounds={selectedRequest.max_negotiation_rounds || 5}
                          currentRound={selectedRequest.negotiation_round || 0}
                        />
                      </Box>
                    </Box>
                  </>
                )}
              </Stack>
            </DialogContent>
            {['responded', 'negotiating'].includes(selectedRequest.status) && selectedRequest.quote_amount && (
              <Box sx={{ px: 3, pb: 1 }}>
                <QuoteNegotiationActions
                  kind="quote" id={selectedRequest.id}
                  total={selectedRequest.quote_amount} currency={selectedRequest.quote_currency || 'USD'}
                />
              </Box>
            )}
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setSelectedRequest(null)}>Close</Button>

              {/* All communication stays on-platform: chat with the provider
                  instead of swapping phone numbers inside quote messages. */}
              {(selectedRequest.contractor_id || selectedRequest.verifier_id) && (
                <Button
                  variant="outlined"
                  startIcon={<Message />}
                  onClick={() => navigate(`/messages?userId=${selectedRequest.contractor_id || selectedRequest.verifier_id}${selectedRequest.project_id ? `&projectId=${selectedRequest.project_id}` : ''}`)}
                >
                  Message {selectedIsVerifier ? 'Verifier' : 'Contractor'}
                </Button>
              )}

              {/* Withdraw for pending/responded/negotiating quotes */}
              {['pending', 'responded', 'negotiating'].includes(selectedRequest.status) && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => setConfirmAction({ type: 'withdraw', request: selectedRequest })}
                >
                  Withdraw Request
                </Button>
              )}

              {/* Actions for responded quotes — accept/counter only make sense
                  once there IS a price. A site-visit-first response is handled
                  by the visit panel above. */}
              {isClientTurn(selectedRequest) && Boolean(selectedRequest.quote_amount) && (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<ThumbDown />}
                    onClick={() => setConfirmAction({ type: 'decline', request: selectedRequest })}
                  >
                    Decline
                  </Button>
                  {selectedRequest.negotiation_round < (selectedRequest.max_negotiation_rounds || 5) && (
                    <Button
                      variant="outlined"
                      startIcon={<SwapHoriz />}
                      onClick={() => setCounterOfferDialogOpen(true)}
                      sx={{ borderColor: '#f59e0b', color: '#f59e0b' }}
                    >
                      Counter-Offer
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<ThumbUp />}
                    onClick={() => setConfirmAction({ type: 'accept', request: selectedRequest })}
                    sx={{ bgcolor: '#43B581', '&:hover': { bgcolor: '#3aa370' } }}
                  >
                    Accept Quote
                  </Button>
                </>
              )}

              {/* Action for accepted quotes */}
              {selectedRequest.status === 'accepted' && (
                <Button
                  variant="contained"
                  startIcon={<CheckCircle />}
                  onClick={() => {
                    setSelectedRequest(null);
                    if (selectedRequest.project_id) {
                      navigate(`/projects/${selectedRequest.project_id}`);
                    } else {
                      navigate('/dashboard');
                    }
                  }}
                  sx={{ bgcolor: '#43B581', '&:hover': { bgcolor: '#3aa370' } }}
                >
                  Go to Project
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Accept/Decline/Withdraw Confirmation Dialog */}
      <Dialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmAction?.type === 'accept' && 'Accept Quote?'}
          {confirmAction?.type === 'decline' && 'Decline Quote?'}
          {confirmAction?.type === 'withdraw' && 'Withdraw Quote Request?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmAction?.type === 'accept'
              ? `Are you sure you want to accept this quote of ${formatCurrency(confirmAction?.request?.quote_amount, confirmAction?.request?.quote_currency)}?`
              : confirmAction?.type === 'withdraw'
                ? 'Are you sure you want to withdraw this quote request? The contractor/verifier will be notified.'
                : 'Are you sure you want to decline this quote? This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmAction?.type === 'accept' ? 'success' : 'error'}
            onClick={() => {
              const { type, request } = confirmAction;
              setConfirmAction(null);
              if (type === 'accept') handleAccept(request);
              else if (type === 'withdraw') handleWithdraw(request);
              else handleDecline(request);
            }}
          >
            {confirmAction?.type === 'accept' && 'Accept'}
            {confirmAction?.type === 'decline' && 'Decline'}
            {confirmAction?.type === 'withdraw' && 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Counter-Offer Dialog */}
      <CounterOfferDialog
        open={counterOfferDialogOpen}
        onClose={() => setCounterOfferDialogOpen(false)}
        quoteRequest={selectedRequest}
        onSuccess={() => {
          setCounterOfferDialogOpen(false);
          setSelectedRequest(null);
          loadQuoteRequests();
        }}
      />
    </Container>
  );
};

export default MyQuoteRequestsPage;
